import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { signInWithProvider } from '../../lib/oauth';
import { isNative, OAUTH_REDIRECT_NATIVE, OAUTH_REDIRECT_WEB } from '../../lib/platform';
import posthog from '../../lib/posthog';
import { Button, Spinner, Input, Checkbox, OAuthButton } from '../../components/design-system';
import { LegalModal } from '../../components/legal/LegalModal';
import { TermsContent } from '../../components/legal/TermsContent';
import { PasswordRules } from '../../components/auth/PasswordRules';
import { friendlyPasswordError, passwordProblem } from '../../lib/passwordPolicy';

/** Register — Google or full name/email/password, with the Sign in/Register toggle above it. */
export function Register() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [googlePending, setGooglePending] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!termsAccepted) {
      setError('You must accept the Terms of Service to continue.');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            terms_accepted_at: new Date().toISOString(),
          },
          // Land on the route that actually completes the auth exchange —
          // without this Supabase falls back to the project Site URL (the app
          // root), which drops the code and silently does nothing. On native,
          // `window.location.origin` is the webview's internal origin (e.g.
          // https://localhost), which means nothing to the Mail app that
          // opens this link — so use the same custom-scheme deep link OAuth
          // already relies on, and let the appUrlOpen handler in lib/native.ts
          // complete the exchange when the app is opened from it.
          emailRedirectTo: isNative ? OAUTH_REDIRECT_NATIVE : OAUTH_REDIRECT_WEB,
        },
      });
      if (authError) throw authError;
      // Supabase doesn't error when the email already belongs to a confirmed
      // account — it silently sends no email instead, to avoid leaking which
      // emails are registered. The one signal it gives us: `identities` comes
      // back empty instead of holding the new identity.
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        setAlreadyRegistered(true);
        return;
      }
      posthog.capture('account_registered');
      setSuccess(true);
    } catch (err: unknown) {
      setError(friendlyPasswordError(err) ?? (err instanceof Error ? err.message : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError('');
    setGooglePending(true);
    try {
      await signInWithProvider('google');
      // On success the page redirects (web) or a deep link completes auth
      // (native), so we intentionally leave the spinner running.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setGooglePending(false);
    }
  }

  // Same fragment pattern as the form below — renders under the persistent
  // AuthLayout header rather than a separate full screen, so the
  // illustration/logo/headline stay in place here too.
  if (alreadyRegistered) {
    return (
      <div className="text-center">
        <h2 className="font-serif text-2xl mb-2" style={{ color: 'rgb(var(--cream-bright))' }}>Account already exists</h2>
        <p className="text-sm mb-6" style={{ color: 'rgba(255, 252, 219, .7)' }}>
          An account with <strong style={{ color: 'rgb(var(--cream-bright))' }}>{email}</strong> already exists. Sign in instead, or reset your password if you've forgotten it.
        </p>
        <div className="flex flex-col gap-3">
          <Button type="button" size="xs" tone="inverted" fullWidth onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm underline underline-offset-2 font-medium"
            style={{ color: 'rgba(255, 252, 219, .7)' }}
          >
            Forgot password?
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center">
        <h2 className="font-serif text-2xl mb-2" style={{ color: 'rgb(var(--cream-bright))' }}>Check your email</h2>
        <p className="text-sm" style={{ color: 'rgba(255, 252, 219, .7)' }}>
          We sent a confirmation link to <strong style={{ color: 'rgb(var(--cream-bright))' }}>{email}</strong>. Click it to activate your account.
        </p>
      </div>
    );
  }

  return (
    <>
      <OAuthButton
        provider="google"
        tone="inverted"
        label="Create account with Google"
        onClick={() => void handleGoogle()}
        disabled={googlePending}
        loading={googlePending}
      />

      <div className="flex items-center gap-3 my-5">
        <span className="h-px flex-1" style={{ background: 'rgba(255, 252, 219, .2)' }} />
        <span className="text-xs uppercase tracking-wide" style={{ color: 'rgba(255, 252, 219, .5)' }}>or</span>
        <span className="h-px flex-1" style={{ background: 'rgba(255, 252, 219, .2)' }} />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          id="fullName"
          type="text"
          label="Full Name"
          tone="inverted"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="John Travolta"
          required
          autoComplete="name"
        />
        <Input
          id="email"
          type="email"
          label="Email"
          tone="inverted"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
        <Input
          id="password"
          type="password"
          label="Password"
          tone="inverted"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          aria-describedby="password-rules"
        />
        <PasswordRules password={password} tone="inverted" />

        <Checkbox
          tone="inverted"
          checked={termsAccepted}
          onChange={(checked) => { setTermsAccepted(checked); if (checked) setError(''); }}
          label={
            <>
              I agree to the{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="underline underline-offset-2 font-medium"
                style={{ color: 'rgb(var(--cream-bright))' }}
              >
                Terms of Service
              </button>
            </>
          }
        />

        {error && <p className="text-sm text-crimson">{error}</p>}
        <Button
          type="submit"
          size="xs"
          tone="inverted"
          fullWidth
          disabled={loading || !termsAccepted}
          className="mt-2"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Creating account…
            </span>
          ) : (
            'Create account'
          )}
        </Button>
      </form>

      <LegalModal open={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service">
        <TermsContent />
      </LegalModal>
    </>
  );
}

export default Register;
